import { getBaseUrl } from '../utils/utils';
import { UrlReloadService } from './url-reload-service';

export interface LogData {
  browserLogLevel: string;
  userAgent?: string;
  [key: string]: string;
}

export class DebugLog {
  errorMessage: string;
  signingMethod: string;
  type: string;
  constructor(errorMessage: string, signingMethod: string, type: string) {
    this.errorMessage = errorMessage;
    this.signingMethod = signingMethod;
    this.type = type;
  }
}

export class LogCollectorService {
  readonly maxLogCount = 2;
  logCount = 0;
  basePath = location.hostname === 'localhost' ? `/api/msp/` : getBaseUrl();
  isHandlingRequest = false;
  reloadService = new UrlReloadService(new URL(window.location.href));

  private send(logData: LogData): void {
    if (!this.isHandlingRequest && this.logCount < this.maxLogCount) {
      this.isHandlingRequest = true;
      this.logCount++;
      const formData = new URLSearchParams();
      for (const key in logData) {
        if (logData[key] !== '') {
          formData.append(key, logData[key]);
        }
      }
      formData.append('userAgent', navigator.userAgent);
      fetch(`${this.basePath}qsl_log_front_end`, {
        method: 'POST',
        body: formData,
        headers: {
          'x-rabo-xsrfp': this.getCookieValue('RABOXSRFP'),
        },
      })
        .then(() => {
          this.isHandlingRequest = false;
        })
        .catch((error) => {
          this.isHandlingRequest = false;
          console.error('Error trying to post log:', error);
        });
    }
  }

  reportError(ev: Event | ErrorEvent): void {
    if (ev instanceof ErrorEvent) {
      // To report script errors
      this.send({
        browserLogLevel: 'ERROR',
        type: 'ERROR',
        colno: ev.colno ? String(ev.colno) : '',
        filename: ev.filename ? ev.filename.split('/').pop() : '',
        lineno: ev.lineno ? String(ev.lineno) : '',
        message: this.maskNumbers(ev.message || ''),
        stack:
          ev.error && ev.error instanceof Error && ev.error.stack
            ? this.maskNumbers(ev.error.stack).substring(0, 250)
            : '',
      });
    } else if (ev instanceof Event && ev.target instanceof HTMLScriptElement) {
      // To report script loading errors (404 etc)
      this.send({
        browserLogLevel: 'ERROR',
        type: 'LOAD_ERROR',
        src: ev.target.src,
      });
    }
  }

  reportUnhandledRejection(error: Error): void {
    this.send({
      browserLogLevel: 'ERROR',
      type: 'PROMISE_UNHANDLED_REJECTION',
      name: error.name || '',
      message: this.maskNumbers(error.message || ''),
      stack: this.maskNumbers(error.stack || '').substring(0, 250),
    });
  }

  reportDebugLog(debugLog: DebugLog): void {
    this.send({
      browserLogLevel: 'DEBUG',
      type: debugLog.type,
      message: debugLog.errorMessage,
      activeSignMethod: debugLog.signingMethod,
    });
  }

  addEventListeners(): void {
    if (window) {
      window.addEventListener(
        'error',
        (event: ErrorEvent) => {
          if (event.message === 'Script error.') {
            // Ignore Safari crossorigin script errors from browser plugins. The message of these errors is always "Script error." to prevent error leaking.
            return;
          }
          this.reportError(event);
        },
        true,
      );
      window.addEventListener(
        'unhandledrejection',
        (event: PromiseRejectionEvent) => {
          if (
            event.reason &&
            event.reason instanceof Error &&
            (!event.reason.message.includes('isProxied') ||
              this.reloadService.isMaxReloadExceeded)
          ) {
            this.reportUnhandledRejection(event.reason);
          }
        },
        true,
      );
    }
  }

  maskNumbers(str: string): string {
    return str.replace(/[0-9]/g, '*');
  }

  getCookieValue(cookieName: string): string {
    const name = `${cookieName}=`;
    return (
      document.cookie
        .split(';')
        .find((cookie) => cookie.trim().indexOf(name) === 0) || ''
    )
      .trim()
      .substring(name.length);
  }
}
